import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Download, 
  Eye, 
  Trash2, 
  CheckCircle, 
  XCircle, 
  Clock,
  User,
  MapPin,
  CreditCard,
  Calendar,
  Edit,
  X,
  Save,
  FileText,
  Mail,
  Send,
  Maximize2,
  Minimize2
} from 'lucide-react';
import axios from 'axios';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useExpandable } from '../../hooks/useExpandable';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import AdminNifLinksPanel from './AdminNifLinksPanel';
import { getAssetUrl } from '../../utils/assetUrl';
import { useTranslation } from 'react-i18next';

interface AdvogadoOption {
  _id: string;
  nome: string;
  oabNumero: string;
  oabEstado: string;
  cedulaProfissional: string;
  logo?: string;
}

type AdvogadoDocumentAction = 'procuracao' | 'email';

interface NIFData {
  _id: string;
  nome: string;
  sobrenome: string;
  dataNascimento: string;
  email: string;
  passaporteNumero: string;
  passaporteValidade: string;
  documentoPassaporte?: string | null;
  documentoComprovanteResidencia?: string | null;
  endereco: {
    cep: string;
    logradouro: string;
    numero: string;
    complemento: string;
    bairro: string;
    cidade: string;
    estado: string;
    uf: string;
  };
  status: 'pendente' | 'em_analise' | 'aprovado' | 'rejeitado';
  createdAt: string;
  updatedAt: string;
}

const AdminNIF: React.FC = () => {
  const { t } = useTranslation();
  const [nifs, setNifs] = useState<NIFData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedNIF, setSelectedNIF] = useState<NIFData | null>(null);
  
  // Estados para edição
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingNif, setEditingNif] = useState<NIFData | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<NIFData>>({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [generatingProcuracao, setGeneratingProcuracao] = useState<string | null>(null);
  const [generatingPDF, setGeneratingPDF] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [pdfProgress, setPdfProgress] = useState(0);
  const [sendingEmail, setSendingEmail] = useState<string | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedNifForEmail, setSelectedNifForEmail] = useState<NIFData | null>(null);
  const [emailData, setEmailData] = useState({
    to: '',
    subject: 'Procuração NIF - Documento Gerado',
    message: ''
  });
  const [showAdvogadoModal, setShowAdvogadoModal] = useState(false);
  const [advogadosList, setAdvogadosList] = useState<AdvogadoOption[]>([]);
  const [loadingAdvogados, setLoadingAdvogados] = useState(false);
  const [selectedAdvogadoId, setSelectedAdvogadoId] = useState<string | null>(null);
  const [pendingNif, setPendingNif] = useState<NIFData | null>(null);

  const { isExpanded: isDetailModalExpanded, setIsExpanded: setIsDetailModalExpanded, toggleExpanded: toggleDetailModalExpanded } = useExpandable();
  useEscapeKey(() => { setSelectedNIF(null); setIsDetailModalExpanded(false); }, !!selectedNIF);

  const { isExpanded: isEditModalExpanded, setIsExpanded: setIsEditModalExpanded, toggleExpanded: toggleEditModalExpanded } = useExpandable();
  useEscapeKey(() => closeEditModal(), isEditModalOpen && !!editingNif);

  const { isExpanded: isAdvogadoModalExpanded, setIsExpanded: setIsAdvogadoModalExpanded, toggleExpanded: toggleAdvogadoModalExpanded } = useExpandable();
  useEscapeKey(() => closeAdvogadoModal(), showAdvogadoModal && !!pendingNif);

  const { isExpanded: isEmailModalExpanded, setIsExpanded: setIsEmailModalExpanded, toggleExpanded: toggleEmailModalExpanded } = useExpandable();
  useEscapeKey(() => {
    setShowEmailModal(false);
    setSelectedNifForEmail(null);
    setSelectedAdvogadoId(null);
    setIsEmailModalExpanded(false);
  }, showEmailModal && !!selectedNifForEmail);
  const [pendingAction, setPendingAction] = useState<AdvogadoDocumentAction | null>(null);
  
  const notifications = useNotifications();
  const confirm = useConfirm();

  // Documentos do cliente (passaporte/comprovante) não são mais públicos em /uploads: são entregues
  // por rota autenticada. <a href> não envia o Bearer, então baixamos como blob e abrimos localmente.
  const openNifDocument = async (nifId: string, tipo: 'passaporte' | 'comprovante') => {
    try {
      const res = await axios.get(`/nif/${nifId}/documento/${tipo}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Erro ao abrir documento NIF:', error);
      notifications.error.settingsUpdate('Erro ao abrir o documento');
    }
  };

  const statusOptions = [
    { value: '', label: 'Todos os status' },
    { value: 'pendente', label: 'Pendente' },
    { value: 'em_analise', label: 'Em Análise' },
    { value: 'aprovado', label: 'Aprovado' },
    { value: 'rejeitado', label: 'Rejeitado' }
  ];

  const statusColors = {
    pendente: 'bg-yellow-100 text-yellow-800',
    em_analise: 'bg-blue-100 text-blue-800',
    aprovado: 'bg-green-100 text-green-800',
    rejeitado: 'bg-red-100 text-red-800'
  };

  useEffect(() => {
    fetchNIFs();
  }, [currentPage, searchTerm, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNIFs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter })
      });
      
      const response = await axios.get(`/nif/list?${params}`);
      setNifs(response.data.nifs);
      setTotalPages(response.data.pagination.pages);
    } catch (err) {
      console.error('Erro ao carregar formulários NIF:', err);
      notifications.error.network();
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (nifId: string, newStatus: string) => {
    try {
      await axios.put(`/nif/${nifId}/status`, { status: newStatus });
      notifications.success.settingsUpdated();
      fetchNIFs();
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
      notifications.error.settingsUpdate('Erro ao atualizar status');
    }
  };

  const handleDelete = async (nifId: string) => {
    if (!(await confirm({ title: 'Excluir formulário NIF', message: 'Este formulário será excluído permanentemente.', confirmText: 'Excluir', variant: 'danger' }))) return;

    try {
      await axios.delete(`/nif/${nifId}`);
      notifications.success.settingsUpdated();
      fetchNIFs();
    } catch (error) {
      console.error('Erro ao deletar formulário:', error);
      notifications.error.settingsUpdate('Erro ao deletar formulário');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pendente':
        return <Clock className="w-4 h-4" />;
      case 'em_analise':
        return <Eye className="w-4 h-4" />;
      case 'aprovado':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejeitado':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const generatePDF = async (nif: NIFData) => {
    try {
      console.log('Gerando PDF para:', nif.nome, nif.sobrenome);
      
      // Iniciar animação de progresso
      setGeneratingPDF(nif._id);
      setPdfProgress(0);
      
      // Simular progresso
      const progressInterval = setInterval(() => {
        setPdfProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 15;
        });
      }, 200);
      
      // Usar fetch diretamente para evitar problemas com interceptor do axios
      const token = localStorage.getItem('token');
      // Usar baseURL do axios que já está configurado
      const apiUrl = axios.defaults.baseURL || '/neia/api';
      const response = await fetch(`${apiUrl}/nif/${nif._id}/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        }
      });
      
      console.log('Resposta recebida:', response.status, response.statusText);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Verificar se o Content-Type é PDF
      const contentType = response.headers.get('content-type');
      console.log('Content-Type:', contentType);
      
      if (!contentType || !contentType.includes('application/pdf')) {
        console.error('Resposta não é um PDF:', contentType);
        const text = await response.text();
        console.error('Resposta como texto:', text.substring(0, 200));
        notifications.error.settingsUpdate('Erro: servidor não retornou PDF válido');
        return;
      }
      
      // Converter para blob
      const blob = await response.blob();
      console.log('Blob criado, tamanho:', blob.size, 'tipo:', blob.type);
      
      // Verificar se o blob é válido
      if (blob.size === 0) {
        notifications.error.settingsUpdate('Erro: PDF vazio recebido do servidor');
        return;
      }
      
      // Completar progresso
      clearInterval(progressInterval);
      setPdfProgress(100);
      
      // Criar URL para o blob
      const url = window.URL.createObjectURL(blob);
      
      // Criar link temporário para download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nif-${nif.nome}-${nif.sobrenome}.pdf`);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Limpar
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
        setGeneratingPDF(null);
        setPdfProgress(0);
      }, 100);
      
      notifications.success.settingsUpdated();
    } catch (error: any) {
      console.error('Erro ao gerar PDF:', error);
      notifications.error.settingsUpdate('Erro ao gerar PDF: ' + error.message);
      setGeneratingPDF(null);
      setPdfProgress(0);
    }
  };

  const fetchAdvogadosForSelection = async (): Promise<AdvogadoOption[]> => {
    const response = await axios.get<AdvogadoOption[]>('/advogado/all');
    return response.data;
  };

  const openAdvogadoSelector = async (nif: NIFData, action: AdvogadoDocumentAction) => {
    try {
      setLoadingAdvogados(true);
      setPendingNif(nif);
      setPendingAction(action);
      const list = await fetchAdvogadosForSelection();

      if (list.length === 0) {
        notifications.error.settingsUpdate(
          'Nenhum advogado cadastrado. Cadastre em Admin → Advogados.'
        );
        return;
      }

      setAdvogadosList(list);
      setSelectedAdvogadoId(list[0]._id);
      setShowAdvogadoModal(true);
    } catch (error) {
      console.error('Erro ao carregar advogados:', error);
      notifications.error.settingsUpdate('Erro ao carregar lista de advogados');
    } finally {
      setLoadingAdvogados(false);
    }
  };

  const closeAdvogadoModal = () => {
    setShowAdvogadoModal(false);
    setPendingNif(null);
    setPendingAction(null);
    setSelectedAdvogadoId(null);
    setAdvogadosList([]);
    setIsAdvogadoModalExpanded(false);
  };

  const confirmAdvogadoSelection = async () => {
    if (!pendingNif || !selectedAdvogadoId || !pendingAction) return;

    const nif = pendingNif;
    const advogadoId = selectedAdvogadoId;
    const action = pendingAction;

    setShowAdvogadoModal(false);
    setPendingNif(null);
    setPendingAction(null);
    setAdvogadosList([]);
    setIsAdvogadoModalExpanded(false);

    if (action === 'procuracao') {
      await generateProcuracao(nif, advogadoId);
    } else {
      setSelectedAdvogadoId(advogadoId);
      setSelectedNifForEmail(nif);
      setEmailData({
        to: nif.email,
        subject: `Procuração NIF - ${nif.nome} ${nif.sobrenome}`,
        message: `Prezado(a) ${nif.nome} ${nif.sobrenome},\n\nSegue em anexo a procuração para solicitação do NIF em Portugal.\n\nAtenciosamente,\nEquipe Europa Solutions`
      });
      setShowEmailModal(true);
    }
  };

  // Função para gerar procuração
  const generateProcuracao = async (nif: NIFData, advogadoId: string) => {
    try {
      console.log('Gerando procuração para:', nif.nome, nif.sobrenome);
      
      // Iniciar animação de progresso
      setGeneratingProcuracao(nif._id);
      setProgress(0);
      
      // Simular progresso
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 15;
        });
      }, 200);
      
      // Usar fetch diretamente para evitar problemas com interceptor do axios
      const token = localStorage.getItem('token');
      // Usar baseURL do axios que já está configurado
      const apiUrl = axios.defaults.baseURL || '/neia/api';
      const query = new URLSearchParams({ advogadoId });
      const response = await fetch(`${apiUrl}/nif/${nif._id}/procuracao?${query}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        }
      });
      
      console.log('Resposta recebida:', response.status, response.statusText);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Verificar se o Content-Type é PDF
      const contentType = response.headers.get('content-type');
      console.log('Content-Type:', contentType);
      
      if (!contentType || !contentType.includes('application/pdf')) {
        console.error('Resposta não é um PDF:', contentType);
        const text = await response.text();
        console.error('Resposta como texto:', text.substring(0, 200));
        notifications.error.settingsUpdate('Erro: servidor não retornou PDF válido');
        return;
      }
      
      // Converter para blob
      const blob = await response.blob();
      console.log('Blob criado, tamanho:', blob.size, 'tipo:', blob.type);
      
      // Verificar se o blob é válido
      if (blob.size === 0) {
        notifications.error.settingsUpdate('Erro: PDF vazio recebido do servidor');
        return;
      }
      
      // Completar progresso
      clearInterval(progressInterval);
      setProgress(100);
      
      // Criar URL para o blob
      const url = window.URL.createObjectURL(blob);
      
      // Criar link temporário para download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `procuracao-${nif.nome}-${nif.sobrenome}.pdf`);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Limpar
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
        setGeneratingProcuracao(null);
        setProgress(0);
      }, 100);
      
      notifications.success.settingsUpdated();
    } catch (error: any) {
      console.error('Erro ao gerar procuração:', error);
      notifications.error.settingsUpdate('Erro ao gerar procuração: ' + error.message);
      setGeneratingProcuracao(null);
      setProgress(0);
    }
  };

  // Função para enviar email
  const sendEmail = async (nif: NIFData) => {
    if (!selectedAdvogadoId) {
      notifications.error.settingsUpdate('Selecione um advogado para gerar o documento');
      return;
    }

    try {
      setSendingEmail(nif._id);
      
      await axios.post(`/nif/${nif._id}/send-email`, {
        to: emailData.to,
        subject: emailData.subject,
        message: emailData.message,
        advogadoId: selectedAdvogadoId
      });
      
      notifications.success.settingsUpdated();
      setShowEmailModal(false);
      setSelectedNifForEmail(null);
      setSelectedAdvogadoId(null);
      setIsEmailModalExpanded(false);
      setEmailData({
        to: '',
        subject: 'Procuração NIF - Documento Gerado',
        message: ''
      });
    } catch (error: any) {
      console.error('Erro ao enviar email:', error);
      notifications.error.settingsUpdate('Erro ao enviar email: ' + error.message);
    } finally {
      setSendingEmail(null);
    }
  };

  const requestProcuracao = (nif: NIFData) => {
    if (loadingAdvogados) return;
    openAdvogadoSelector(nif, 'procuracao');
  };

  const requestEmailWithProcuracao = (nif: NIFData) => {
    if (loadingAdvogados) return;
    openAdvogadoSelector(nif, 'email');
  };

  // Funções para edição
  const openEditModal = (nif: NIFData) => {
    setEditingNif(nif);
    setEditFormData({
      nome: nif.nome,
      sobrenome: nif.sobrenome,
      dataNascimento: nif.dataNascimento.split('T')[0], // Converter para formato de input date
      email: nif.email,
      passaporteNumero: nif.passaporteNumero,
      passaporteValidade: nif.passaporteValidade.split('T')[0],
      endereco: { ...nif.endereco },
      status: nif.status
    });
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingNif(null);
    setEditFormData({});
    setIsEditModalExpanded(false);
  };

  const handleEditInputChange = (field: string, value: any) => {
    if (field.startsWith('endereco.')) {
      const enderecoField = field.split('.')[1];
      setEditFormData(prev => ({
        ...prev,
        endereco: {
          ...prev.endereco!,
          [enderecoField]: value
        }
      }));
    } else {
      setEditFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleUpdateNif = async () => {
    if (!editingNif) return;

    try {
      setIsUpdating(true);
      
      const response = await axios.put(`/nif/${editingNif._id}`, editFormData);
      
      // Atualizar a lista local
      setNifs(prev => prev.map(nif => 
        nif._id === editingNif._id ? response.data.nif : nif
      ));
      
      notifications.success.settingsUpdated();
      closeEditModal();
      
    } catch (error: any) {
      console.error('Erro ao atualizar NIF:', error);
      notifications.error.settingsUpdate(
        error.response?.data?.message || 'Erro ao atualizar formulário NIF'
      );
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Breadcrumbs />
      </div>

      <AdminNifLinksPanel />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
        <h1 className="text-3xl font-bold text-gray-900">{t('admin.nif')}</h1>
        <div className="text-gray-600 text-sm sm:text-base">
          Total: {nifs.length} formulários
        </div>
      </div>

      {/* Filtros */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar por nome, sobrenome ou passaporte..."
            className="input-field pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input-field w-full sm:w-48"
        >
          {statusOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Lista de Formulários */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {nifs.map((nif) => (
            <div key={nif._id} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <User className="w-5 h-5 text-gray-500 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      {nif.nome} {nif.sobrenome}
                    </h3>
                    <span className={`ml-3 px-2 py-1 rounded-full text-xs font-medium flex items-center ${statusColors[nif.status]}`}>
                      {getStatusIcon(nif.status)}
                      <span className="ml-1 capitalize">{nif.status.replace('_', ' ')}</span>
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                    <div className="flex items-center">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Passaporte: {nif.passaporteNumero}
                    </div>
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-2" />
                      {nif.endereco.cidade}, {nif.endereco.uf}
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      {formatDate(nif.createdAt)}
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-1">
                  <button
                    onClick={() => setSelectedNIF(nif)}
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                    title="Ver detalhes"
                  >
                    <Eye className="w-5 h-5" />
                  </button>

                  <button
                    onClick={() => openEditModal(nif)}
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                    title="Editar"
                  >
                    <Edit className="w-5 h-5" />
                  </button>

                  <button
                    onClick={() => generatePDF(nif)}
                    disabled={generatingPDF === nif._id}
                    className={`p-2 rounded-lg transition-colors duration-200 ${
                      generatingPDF === nif._id
                        ? 'text-green-400 cursor-not-allowed'
                        : 'text-green-600 hover:bg-green-100'
                    }`}
                    title={generatingPDF === nif._id ? "Gerando PDF..." : "Gerar PDF"}
                  >
                    {generatingPDF === nif._id ? (
                      <div className="w-5 h-5 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Download className="w-5 h-5" />
                    )}
                  </button>

                  <button
                    onClick={() => requestProcuracao(nif)}
                    disabled={generatingProcuracao === nif._id || loadingAdvogados}
                    className={`p-2 rounded-lg transition-colors duration-200 ${
                      generatingProcuracao === nif._id
                        ? 'text-purple-400 cursor-not-allowed'
                        : 'text-purple-600 hover:bg-purple-100'
                    }`}
                    title={generatingProcuracao === nif._id ? "Gerando PDF..." : "Gerar Procuração"}
                  >
                    {generatingProcuracao === nif._id ? (
                      <div className="w-5 h-5 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <FileText className="w-5 h-5" />
                    )}
                  </button>

                  <button
                    onClick={() => requestEmailWithProcuracao(nif)}
                    disabled={sendingEmail === nif._id || loadingAdvogados}
                    className={`p-2 rounded-lg transition-colors duration-200 ${
                      sendingEmail === nif._id
                        ? 'text-blue-400 cursor-not-allowed'
                        : 'text-blue-600 hover:bg-blue-100'
                    }`}
                    title={sendingEmail === nif._id ? "Enviando email..." : "Enviar Procuração por Email"}
                  >
                    {sendingEmail === nif._id ? (
                      <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Mail className="w-5 h-5" />
                    )}
                  </button>

                  <button
                    onClick={() => handleDelete(nif._id)}
                    className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors duration-200"
                    title="Deletar"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Barra de Progresso para PDF */}
                {generatingPDF === nif._id && (
                  <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-green-700">
                        Gerando PDF do Formulário...
                      </span>
                      <span className="text-sm text-green-600">
                        {Math.round(pdfProgress)}%
                      </span>
                    </div>
                    <div className="w-full bg-green-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${pdfProgress}%` }}
                      ></div>
                    </div>
                    <div className="mt-2 text-xs text-green-600">
                      Preparando documento com dados do formulário...
                    </div>
                  </div>
                )}
                
                {/* Barra de Progresso para Procuração */}
                {generatingProcuracao === nif._id && (
                  <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-purple-700">
                        Gerando PDF da Procuração...
                      </span>
                      <span className="text-sm text-purple-600">
                        {Math.round(progress)}%
                      </span>
                    </div>
                    <div className="w-full bg-purple-200 rounded-full h-2">
                      <div 
                        className="bg-purple-600 h-2 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    <div className="mt-2 text-xs text-purple-600">
                      Preparando documento com dados do advogado...
                    </div>
                  </div>
                )}
              </div>
              
              {/* Ações de Status */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  {statusOptions.slice(1).map(option => (
                    <button
                      key={option.value}
                      onClick={() => handleStatusChange(nif._id, option.value)}
                      className={`px-3 py-1 text-xs rounded-lg transition-colors duration-200 ${
                        nif.status === option.value
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center">
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                  currentPage === page
                    ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próxima
            </button>
          </nav>
        </div>
      )}

      {/* Modal de Detalhes */}
      {selectedNIF && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className={`bg-white rounded-t-2xl sm:rounded-lg p-4 sm:p-6 w-full overflow-y-auto ${
            isDetailModalExpanded ? 'max-w-[95vw] max-h-[95vh]' : 'sm:max-w-2xl max-h-[90vh]'
          }`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Detalhes do Formulário NIF</h2>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={toggleDetailModalExpanded}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  title={isDetailModalExpanded ? 'Restaurar' : 'Expandir'}
                >
                  {isDetailModalExpanded ? <Minimize2 className="w-6 h-6" /> : <Maximize2 className="w-6 h-6" />}
                </button>
                <button
                  onClick={() => { setSelectedNIF(null); setIsDetailModalExpanded(false); }}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  title="Fechar"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Dados Pessoais</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Nome:</span>
                    <p className="font-medium">{selectedNIF.nome} {selectedNIF.sobrenome}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Data de Nascimento:</span>
                    <p className="font-medium">{new Date(selectedNIF.dataNascimento).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <p className="font-medium">{selectedNIF.email}</p>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Passaporte</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Número:</span>
                    <p className="font-medium">{selectedNIF.passaporteNumero}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Validade:</span>
                    <p className="font-medium">{new Date(selectedNIF.passaporteValidade).toLocaleDateString('pt-BR')}</p>
                  </div>
                </div>
              </div>

              {(selectedNIF.documentoPassaporte || selectedNIF.documentoComprovanteResidencia) && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Documentos enviados</h3>
                  <div className="flex flex-wrap gap-4 text-sm">
                    {selectedNIF.documentoPassaporte && (
                      <button
                        type="button"
                        onClick={() => openNifDocument(selectedNIF._id, 'passaporte')}
                        className="inline-flex items-center text-primary-600 hover:underline"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Passaporte (cópia)
                      </button>
                    )}
                    {selectedNIF.documentoComprovanteResidencia && (
                      <button
                        type="button"
                        onClick={() => openNifDocument(selectedNIF._id, 'comprovante')}
                        className="inline-flex items-center text-primary-600 hover:underline"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Comprovante de residência
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Endereço</h3>
                <div className="text-sm">
                  <p className="font-medium">
                    {selectedNIF.endereco.logradouro}, {selectedNIF.endereco.numero}
                    {selectedNIF.endereco.complemento && `, ${selectedNIF.endereco.complemento}`}
                  </p>
                  <p className="text-gray-600">
                    {selectedNIF.endereco.bairro} - {selectedNIF.endereco.cidade}/{selectedNIF.endereco.uf}
                  </p>
                  <p className="text-gray-600">CEP: {selectedNIF.endereco.cep}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição */}
      {isEditModalOpen && editingNif && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className={`bg-white rounded-t-2xl sm:rounded-lg p-4 sm:p-6 w-full overflow-y-auto ${
            isEditModalExpanded ? 'max-w-[95vw] max-h-[95vh]' : 'sm:max-w-4xl max-h-[90vh]'
          }`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Editar Formulário NIF</h2>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={toggleEditModalExpanded}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  title={isEditModalExpanded ? 'Restaurar' : 'Expandir'}
                >
                  {isEditModalExpanded ? <Minimize2 className="w-6 h-6" /> : <Maximize2 className="w-6 h-6" />}
                </button>
                <button
                  onClick={closeEditModal}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  title="Fechar"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); handleUpdateNif(); }} className="space-y-6">
              {/* Dados Pessoais */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome *
                  </label>
                  <input
                    type="text"
                    value={editFormData.nome || ''}
                    onChange={(e) => handleEditInputChange('nome', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sobrenome *
                  </label>
                  <input
                    type="text"
                    value={editFormData.sobrenome || ''}
                    onChange={(e) => handleEditInputChange('sobrenome', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Nascimento *
                  </label>
                  <input
                    type="date"
                    value={editFormData.dataNascimento || ''}
                    onChange={(e) => handleEditInputChange('dataNascimento', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={editFormData.email || ''}
                    onChange={(e) => handleEditInputChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={editFormData.status || ''}
                    onChange={(e) => handleEditInputChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="pendente">Pendente</option>
                    <option value="em_analise">Em Análise</option>
                    <option value="aprovado">Aprovado</option>
                    <option value="rejeitado">Rejeitado</option>
                  </select>
                </div>
              </div>

              {/* Dados do Passaporte */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-4">Dados do Passaporte</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Número do Passaporte *
                    </label>
                    <input
                      type="text"
                      value={editFormData.passaporteNumero || ''}
                      onChange={(e) => handleEditInputChange('passaporteNumero', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Validade do Passaporte *
                    </label>
                    <input
                      type="date"
                      value={editFormData.passaporteValidade || ''}
                      onChange={(e) => handleEditInputChange('passaporteValidade', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Endereço */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-4">Endereço</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CEP *
                    </label>
                    <input
                      type="text"
                      value={editFormData.endereco?.cep || ''}
                      onChange={(e) => handleEditInputChange('endereco.cep', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Logradouro *
                    </label>
                    <input
                      type="text"
                      value={editFormData.endereco?.logradouro || ''}
                      onChange={(e) => handleEditInputChange('endereco.logradouro', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Número
                    </label>
                    <input
                      type="text"
                      value={editFormData.endereco?.numero || ''}
                      onChange={(e) => handleEditInputChange('endereco.numero', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Complemento
                    </label>
                    <input
                      type="text"
                      value={editFormData.endereco?.complemento || ''}
                      onChange={(e) => handleEditInputChange('endereco.complemento', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bairro *
                    </label>
                    <input
                      type="text"
                      value={editFormData.endereco?.bairro || ''}
                      onChange={(e) => handleEditInputChange('endereco.bairro', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cidade *
                    </label>
                    <input
                      type="text"
                      value={editFormData.endereco?.cidade || ''}
                      onChange={(e) => handleEditInputChange('endereco.cidade', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estado *
                    </label>
                    <input
                      type="text"
                      value={editFormData.endereco?.estado || ''}
                      onChange={(e) => handleEditInputChange('endereco.estado', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      UF *
                    </label>
                    <input
                      type="text"
                      value={editFormData.endereco?.uf || ''}
                      onChange={(e) => handleEditInputChange('endereco.uf', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Botões */}
              <div className="flex justify-end space-x-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isUpdating}
                  className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Salvando...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      <span>Salvar Alterações</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de seleção de advogado */}
      {showAdvogadoModal && pendingNif && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className={`bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full overflow-y-auto ${
            isAdvogadoModalExpanded ? 'max-w-[95vw] max-h-[95vh]' : 'sm:max-w-lg max-h-[90vh]'
          }`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Selecionar Advogado
                </h3>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={toggleAdvogadoModalExpanded}
                    className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                    title={isAdvogadoModalExpanded ? 'Restaurar' : 'Expandir'}
                  >
                    {isAdvogadoModalExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                  </button>
                  <button
                    type="button"
                    onClick={closeAdvogadoModal}
                    className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                    title="Fechar"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Cliente: <strong>{pendingNif.nome} {pendingNif.sobrenome}</strong>
                {pendingAction === 'email' && ' — o documento será anexado ao email.'}
              </p>
              <div className="space-y-3 mb-6">
                {advogadosList.map(adv => (
                  <label
                    key={adv._id}
                    className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedAdvogadoId === adv._id ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="advogado"
                      checked={selectedAdvogadoId === adv._id}
                      onChange={() => setSelectedAdvogadoId(adv._id)}
                      className="text-blue-600"
                    />
                    {adv.logo ? (
                      <img src={getAssetUrl(adv.logo)} alt="" className="w-12 h-12 object-contain border rounded bg-white" />
                    ) : (
                      <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center">
                        <User className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900">{adv.nome}</p>
                      <p className="text-sm text-gray-600">OAB {adv.oabNumero}/{adv.oabEstado}</p>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeAdvogadoModal}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmAdvogadoSelection}
                  disabled={!selectedAdvogadoId}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {pendingAction === 'procuracao' ? 'Gerar Procuração' : 'Continuar para Email'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Email */}
      {showEmailModal && selectedNifForEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className={`bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full max-h-[90vh] overflow-y-auto ${
            isEmailModalExpanded ? 'max-w-[95vw] max-h-[95vh]' : 'sm:max-w-md'
          }`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Mail className="w-5 h-5 mr-2" />
                  Enviar Procuração por Email
                </h3>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={toggleEmailModalExpanded}
                    className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                    title={isEmailModalExpanded ? 'Restaurar' : 'Expandir'}
                  >
                    {isEmailModalExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmailModal(false);
                      setSelectedNifForEmail(null);
                      setSelectedAdvogadoId(null);
                      setIsEmailModalExpanded(false);
                    }}
                    className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                    title="Fechar"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); sendEmail(selectedNifForEmail); }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email do Cliente *
                  </label>
                  <input
                    type="email"
                    value={emailData.to}
                    onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="cliente@email.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assunto *
                  </label>
                  <input
                    type="text"
                    value={emailData.subject}
                    onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mensagem *
                  </label>
                  <textarea
                    value={emailData.message}
                    onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmailModal(false);
                      setSelectedNifForEmail(null);
                      setSelectedAdvogadoId(null);
                      setIsEmailModalExpanded(false);
                    }}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={sendingEmail === selectedNifForEmail._id}
                    className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sendingEmail === selectedNifForEmail._id ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        <span>Enviando...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>Enviar Email</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
    </div>
  );
};

export default AdminNIF;
